import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type { LeadListItem } from "@shared-core/data/api-types";
import type { LeadListOptions } from "@shared-core/data/adapter";
import { ApiError } from "@shared-core/data/api/errors";
import { useDebouncedValue } from "@shared-core/domain/useDebouncedValue";
import { formatCount } from "@shared-core/domain/formatters";
import { paths } from "@shared-core/routing/routes";
import { ListPagination } from "../app/components/patterns/ListPagination";
import { LeadStageBadge } from "../app/components/domain/leads/LeadStageBadge";
import { LeadKanbanBoard } from "../app/components/domain/leads/LeadKanbanBoard";
import { CreateLeadModal } from "../app/components/domain/leads/CreateLeadModal";
import { AlertTriangle, Plus } from "lucide-react";

// Follows the canonical list-endpoint pattern (see docs/list-endpoints.md)
// for the List view — server-side search/sort/page, no client filtering.
// The Pipeline (kanban) view needs the whole board rather than one page at
// a time, so it fetches with a generously large page size instead of a
// second, unbounded endpoint — a reasonable simplification for a rep's
// number of open leads; worth revisiting if the pipeline ever needs true
// pagination.

type ViewMode = "list" | "pipeline";
const PAGE_SIZE = 25;
// Matches the backend's MAX_PAGE_SIZE (apps/api/app/list_query.py) — the
// same "fetch the whole set" ceiling used by ManufacturingSetupPage. A
// page_size above this is rejected
// with 422 before the action runs.
const PIPELINE_PAGE_SIZE = 200;

function parseView(raw: string | null): ViewMode {
  return raw === "pipeline" ? "pipeline" : "list";
}

export function LeadsPage() {
  const dataAdapter = useDataAdapter();
  const [searchParams, setSearchParams] = useSearchParams();
  const view = parseView(searchParams.get("view"));

  const [leads, setLeads] = useState<LeadListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [firstLoad, setFirstLoad] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearch = useDebouncedValue(searchQuery, 300);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  // The count for the pending-match banner: a dedicated pageSize:1 query for
  // the true `total` of pending-match leads, not a client-side filter of
  // whatever page happens to be loaded (which under-reports once matches
  // exist beyond page 1, and filters a list-endpoint response client-side —
  // forbidden by frontend-architecture.md's "list endpoints" rule).
  const [pendingMatchCount, setPendingMatchCount] = useState(0);
  const [pendingMatchLeadId, setPendingMatchLeadId] = useState<string | null>(
    null,
  );

  const page = Math.max(1, parseInt(searchParams.get("page") ?? "1", 10) || 1);

  const setView = (next: ViewMode) => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (next === "pipeline") {
          params.set("view", "pipeline");
        } else {
          params.delete("view");
        }
        params.set("page", "1");
        return params;
      },
      { replace: true },
    );
  };

  const setPage = (p: number) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("page", String(p));
        return next;
      },
      { replace: true },
    );
  };

  // Any change to what's being viewed restarts pagination from page 1 —
  // except the very first run, which would otherwise stomp a page number
  // just restored from the URL back to 1 the instant the page mounts.
  const skipNextPageReset = useRef(true);
  useEffect(() => {
    if (skipNextPageReset.current) {
      skipNextPageReset.current = false;
      return;
    }
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, view]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      const options: LeadListOptions = {
        page: view === "pipeline" ? 1 : page,
        pageSize: view === "pipeline" ? PIPELINE_PAGE_SIZE : PAGE_SIZE,
      };
      if (debouncedSearch.trim()) options.q = debouncedSearch.trim();
      setLoadError(false);
      try {
        const resp = await dataAdapter.listLeads(options);
        if (cancelled) return;
        setLeads(resp.items);
        setTotal(resp.total);
        setTotalPages(resp.totalPages);
        setPageSize(resp.pageSize);
      } catch (error) {
        if (cancelled) return;
        console.error("sales_leads.list.load_failed", {
          status: error instanceof ApiError ? error.status : undefined,
        });
        setLoadError(true);
      } finally {
        if (!cancelled) setFirstLoad(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [dataAdapter, debouncedSearch, view, page]);

  useEffect(() => {
    let cancelled = false;
    async function loadPendingMatchCount() {
      try {
        const resp = await dataAdapter.listLeads({
          hasPendingMatch: true,
          page: 1,
          pageSize: 1,
        });
        if (cancelled) return;
        setPendingMatchCount(resp.total);
        setPendingMatchLeadId(resp.items[0]?.id ?? null);
      } catch (error) {
        if (cancelled) return;
        console.error("sales_leads.pending_match_count.load_failed", {
          status: error instanceof ApiError ? error.status : undefined,
        });
      }
    }
    loadPendingMatchCount();
    return () => {
      cancelled = true;
    };
  }, [dataAdapter]);

  if (firstLoad) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-muted-foreground">Loading leads...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center justify-between mb-4 gap-4 flex-wrap">
          <h1 className="text-2xl">Leads</h1>
          <div className="flex items-center gap-3">
            <div
              role="tablist"
              aria-label="View"
              className="inline-flex rounded-md border border-border overflow-hidden"
            >
              <button
                type="button"
                role="tab"
                aria-selected={view === "list"}
                onClick={() => setView("list")}
                className={`px-3 py-1.5 text-sm transition-colors ${
                  view === "list"
                    ? "bg-primary text-white"
                    : "bg-surface text-foreground hover:bg-surface-hover"
                }`}
              >
                List
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={view === "pipeline"}
                onClick={() => setView("pipeline")}
                className={`px-3 py-1.5 text-sm transition-colors border-l border-border ${
                  view === "pipeline"
                    ? "bg-primary text-white"
                    : "bg-surface text-foreground hover:bg-surface-hover"
                }`}
              >
                Pipeline
              </button>
            </div>
            <button
              type="button"
              onClick={() => setIsCreateOpen(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-primary text-white rounded-md hover:bg-primary/90 transition-colors"
            >
              <Plus className="size-4" />
              New lead
            </button>
          </div>
        </div>

        <input
          type="text"
          aria-label="Search leads"
          placeholder="Search leads..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="px-3 py-2 border border-border rounded bg-background text-sm w-full sm:w-64"
        />
      </div>

      {loadError && (
        <div className="flex items-center gap-2 text-sm p-4 border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/20 rounded-lg text-red-700 dark:text-red-300">
          <AlertTriangle className="size-4 shrink-0" />
          Couldn't load leads. Please try again.
        </div>
      )}

      {pendingMatchCount > 0 && (
        <div className="flex items-center justify-between gap-4 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800 px-4 py-3">
          <div className="text-sm text-amber-900 dark:text-amber-200">
            <strong>{formatCount(pendingMatchCount)}</strong> lead
            {pendingMatchCount !== 1 ? "s" : ""} may have converted to a
            customer — review the suggested match
            {pendingMatchCount !== 1 ? "es" : ""}.
          </div>
          {pendingMatchLeadId && (
            <Link
              to={paths.leadDetail({ leadId: pendingMatchLeadId })}
              className="text-sm font-medium text-amber-900 dark:text-amber-200 underline shrink-0"
            >
              Review now
            </Link>
          )}
        </div>
      )}

      {view === "list" ? (
        <>
          {total > 0 && (
            <div className="text-sm text-muted-foreground">
              Showing {formatCount((page - 1) * pageSize + 1)}–
              {formatCount(Math.min(page * pageSize, total))} of{" "}
              {formatCount(total)} leads
            </div>
          )}

          {leads.length === 0 ? (
            <div className="text-muted-foreground text-sm p-8 text-center border border-border rounded-lg">
              No leads found
            </div>
          ) : (
            <div className="space-y-2">
              {leads.map((lead) => (
                <Link
                  key={lead.id}
                  to={paths.leadDetail({ leadId: lead.id })}
                  className="flex items-center justify-between gap-4 p-4 border border-border rounded-lg bg-surface hover:border-primary transition-colors"
                >
                  <div className="min-w-0">
                    <div className="font-medium text-foreground truncate">
                      {lead.company_name}
                    </div>
                    {(lead.phone ?? lead.website) && (
                      <div className="text-sm text-muted-foreground truncate">
                        {lead.phone ?? lead.website}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    {lead.has_pending_match && (
                      <span className="text-xs text-amber-700 dark:text-amber-300">
                        Match to review
                      </span>
                    )}
                    <LeadStageBadge stage={lead.stage} />
                  </div>
                </Link>
              ))}
            </div>
          )}

          <ListPagination
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
          />
        </>
      ) : (
        <LeadKanbanBoard leads={leads} />
      )}

      {isCreateOpen && (
        <CreateLeadModal
          onClose={() => setIsCreateOpen(false)}
          onCreated={(lead) => {
            setIsCreateOpen(false);
            setLeads((prev) => [
              {
                id: lead.id,
                lead_id: lead.lead_id,
                company_name: lead.company_name,
                stage: lead.stage,
                owner_user_id: lead.owner_user_id,
                address: lead.address,
                phone: lead.phone,
                website: lead.website,
                interest_tags: lead.interest_tags,
                converted_customer_id: lead.converted_customer_id,
                created_at: lead.created_at,
                updated_at: lead.updated_at,
                has_pending_match: lead.has_pending_match,
              },
              ...prev,
            ]);
            setTotal((t) => t + 1);
          }}
        />
      )}
    </div>
  );
}
